import { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import NetworkMap from "../components/NetworkMap";
import DealForgeLogo from "../components/DealForgeLogo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  ArrowRight, 
  Check,
  AlertTriangle,
  Zap,
  Target,
  Calculator,
  Users,
  Phone,
  Workflow,
  Building2,
  Shield,
  Sparkles,
  Crown,
  ChevronDown,
  Star,
  TrendingUp,
  FileText,
  MessageSquare,
  Landmark,
  HardHat,
  Home,
  Briefcase
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function WaitlistPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const waitlistRef = useRef(null);
  const founderRef = useRef(null);

  // Capture referral code from URL (?ref=DF-XXXXXX)
  const [referralCode, setReferralCode] = useState("");
  const [refApplied, setRefApplied] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralCode(ref.toUpperCase());
    }
  }, [searchParams]);

  // Simple waitlist form
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [investorType, setInvestorType] = useState("");
  const [market, setMarket] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Founder application form
  const [showFounderForm, setShowFounderForm] = useState(false);
  const [founderForm, setFounderForm] = useState({
    fullName: "",
    email: "",
    role: "",
    primaryMarket: "",
    dealsClosedLast12Months: "",
    monthlyVolume: "",
    whyAccess: ""
  });
  const [founderSubmitting, setFounderSubmitting] = useState(false);
  const [founderSubmitted, setFounderSubmitted] = useState(false);
  const [founderError, setFounderError] = useState("");

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    if (!firstName || !email || !investorType || !market) {
      setError("Please fill in all fields");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/waitlist/public-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName,
          email,
          role: investorType,
          market
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to join waitlist");
      }

      setIsSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFounderSubmit = async (e) => {
    e.preventDefault();
    setFounderError("");

    const { fullName, email, role, primaryMarket, dealsClosedLast12Months, monthlyVolume, whyAccess } = founderForm;
    if (!fullName || !email || !role || !primaryMarket || !dealsClosedLast12Months || !monthlyVolume || !whyAccess) {
      setFounderError("Please complete all fields");
      return;
    }

    setFounderSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/waitlist/founder-application`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: fullName,
          email,
          role,
          primary_market: primaryMarket,
          deals_closed_last_12_months: dealsClosedLast12Months,
          monthly_volume: monthlyVolume,
          why_access: whyAccess,
          application_type: "founder_beta",
          referral_code: referralCode || undefined
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to submit application");
      }

      setFounderSubmitted(true);
    } catch (err) {
      setFounderError(err.message);
    } finally {
      setFounderSubmitting(false);
    }
  };

  // Pain points - institutional language
  const painPoints = [
    { text: "Fragmented Data", desc: "Capital metrics scattered across 10+ tools" },
    { text: "Execution Latency", desc: "Slow coordination delays closings" },
    { text: "Pipeline Blind Spots", desc: "No real-time visibility into deal flow" },
    { text: "Capital Inefficiency", desc: "Suboptimal deployment and returns" },
    { text: "Partner Friction", desc: "Manual coordination with title, lenders, contractors" },
    { text: "Performance Opacity", desc: "Unable to measure true capital efficiency" }
  ];

  // Platform features - institutional language
  const features = [
    { icon: Calculator, title: "Capital Model", desc: "Multi-strategy underwriting with scenario analysis" },
    { icon: Target, title: "AI Deal Intelligence", desc: "Risk-adjusted scoring and market positioning" },
    { icon: TrendingUp, title: "Execution Flow", desc: "End-to-end transaction lifecycle management" },
    { icon: FileText, title: "Deal Documentation", desc: "Professional packets with one-click generation" },
    { icon: MessageSquare, title: "Partner Coordination", desc: "Integrated communication across all stakeholders" },
    { icon: Users, title: "Capital Efficiency Index", desc: "Performance metrics that drive better decisions" }
  ];

  // Founder Beta benefits - institutional language
  const founderBenefits = [
    "Direct access to the founding team",
    "Founder pricing locked permanently",
    "Shape the product roadmap",
    "Priority onboarding and support",
    "Early access to all platform releases",
    "Private channel with leadership"
  ];

  // Ecosystem - institutional language
  const ecosystem = [
    { icon: Building2, title: "Investors", desc: "Command center for capital deployment", color: "gold" },
    { icon: Home, title: "Realtors", desc: "Transaction execution & coordination", color: "green" },
    { icon: Landmark, title: "Lenders", desc: "Deal flow & capital placement", color: "blue" },
    { icon: HardHat, title: "Contractors", desc: "Rehab bidding & project management", color: "orange" },
    { icon: Briefcase, title: "Title Companies", desc: "Closing services marketplace", color: "purple" }
  ];

  // Success state
  if (isSubmitted && !showFounderForm) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-lg text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-gold/20 to-gold/5 border border-gold/30 rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-12 h-12 text-gold" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            You're In!
          </h1>
          <p className="text-xl text-white/70 mb-8">
            We've added you to the early access list. Check your inbox for confirmation.
          </p>
          <div className="bg-black/50 border border-white/[0.06] p-6 rounded-2xl mb-8">
            <p className="text-white/50 mb-4">Want priority access and founding member benefits?</p>
            <Button 
              onClick={() => setShowFounderForm(true)}
              className="gold-gradient-btn h-12 px-8 font-bold"
            >
              Apply for Founder Beta
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
          <p className="text-white/40 text-sm">
            We'll be in touch soon with your access details.
          </p>
        </div>
      </div>
    );
  }

  // Founder application success
  if (founderSubmitted) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="max-w-lg text-center">
          <div className="w-24 h-24 bg-gradient-to-br from-purple-500/20 to-purple-500/5 border border-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-8">
            <Crown className="w-12 h-12 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Application Received
          </h1>
          <p className="text-xl text-white/70 mb-6">
            Thanks for applying to the Founder Beta program.
          </p>
          <p className="text-white/50">
            We review applications personally and will be in touch within 48 hours.
          </p>
        </div>
      </div>
    );
  }

  // Founder application form
  if (showFounderForm) {
    return (
      <div className="min-h-screen bg-black py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <button 
            onClick={() => setShowFounderForm(false)}
            className="text-white/50 hover:text-white mb-8 flex items-center gap-2"
          >
            ← Back to waitlist
          </button>
          
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-purple-500/10 border border-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Crown className="w-8 h-8 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Founder Beta Application
            </h1>
            <p className="text-white/50 max-w-md mx-auto">
              We're accepting a limited group of high-volume operators for exclusive early access.
            </p>
          </div>

          <div className="bg-black/50 border border-white/[0.06] p-6 rounded-2xl mb-8">
            <h3 className="font-bold text-white mb-4">Founder Beta Includes:</h3>
            <ul className="space-y-3">
              {founderBenefits.map((benefit, idx) => (
                <li key={idx} className="flex items-center gap-3 text-white/70">
                  <Check className="w-5 h-5 text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] flex-shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>
          </div>

          <form onSubmit={handleFounderSubmit} className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white/70 mb-2 block">Full Name *</Label>
                <Input
                  value={founderForm.fullName}
                  onChange={(e) => setFounderForm({...founderForm, fullName: e.target.value})}
                  placeholder="John Smith"
                  className="bg-black border-white/[0.08] text-white h-12"
                />
              </div>
              <div>
                <Label className="text-white/70 mb-2 block">Email *</Label>
                <Input
                  type="email"
                  value={founderForm.email}
                  onChange={(e) => setFounderForm({...founderForm, email: e.target.value})}
                  placeholder="john@company.com"
                  className="bg-black border-white/[0.08] text-white h-12"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label className="text-white/70 mb-2 block">Primary Role *</Label>
                <Select value={founderForm.role} onValueChange={(v) => setFounderForm({...founderForm, role: v})}>
                  <SelectTrigger className="bg-black border-white/[0.08] text-white h-12">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent className="bg-black border-white/[0.08]">
                    <SelectItem value="wholesaler">Wholesaler</SelectItem>
                    <SelectItem value="fix_flip">Fix & Flip Investor</SelectItem>
                    <SelectItem value="buy_hold">Buy & Hold Investor</SelectItem>
                    <SelectItem value="operator">Real Estate Operator</SelectItem>
                    <SelectItem value="agent_investor">Agent-Investor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-white/70 mb-2 block">Primary Market *</Label>
                <Input
                  value={founderForm.primaryMarket}
                  onChange={(e) => setFounderForm({...founderForm, primaryMarket: e.target.value})}
                  placeholder="e.g. Charlotte, NC"
                  className="bg-black border-white/[0.08] text-white h-12"
                />
              </div>
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Deals closed in the last 12 months *</Label>
              <Select value={founderForm.dealsClosedLast12Months} onValueChange={(v) => setFounderForm({...founderForm, dealsClosedLast12Months: v})}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white h-12">
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent className="bg-black border-white/[0.08]">
                  <SelectItem value="0-2">0–2</SelectItem>
                  <SelectItem value="3-10">3–10</SelectItem>
                  <SelectItem value="11-25">11–25</SelectItem>
                  <SelectItem value="25+">25+</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Monthly Deal Volume *</Label>
              <Select value={founderForm.monthlyVolume} onValueChange={(v) => setFounderForm({...founderForm, monthlyVolume: v})}>
                <SelectTrigger className="bg-black border-white/[0.08] text-white h-12">
                  <SelectValue placeholder="Select volume" />
                </SelectTrigger>
                <SelectContent className="bg-black border-white/[0.08]">
                  <SelectItem value="1-2">1-2 deals/month</SelectItem>
                  <SelectItem value="3-5">3-5 deals/month</SelectItem>
                  <SelectItem value="6-10">6-10 deals/month</SelectItem>
                  <SelectItem value="10+">10+ deals/month</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-white/70 mb-2 block">Why do you want early access? *</Label>
              <Textarea
                value={founderForm.whyAccess}
                onChange={(e) => setFounderForm({...founderForm, whyAccess: e.target.value})}
                placeholder="Tell us about your operation and what you're looking for..."
                className="bg-black border-white/[0.08] text-white min-h-[120px]"
              />
            </div>

            {founderError && (
              <p className="text-[#FF3B30] text-sm">{founderError}</p>
            )}

            <Button 
              type="submit"
              disabled={founderSubmitting}
              className="w-full h-14 bg-purple-600 hover:bg-purple-500 text-white font-bold text-lg"
            >
              {founderSubmitting ? "Submitting..." : "Submit Application"}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>DealForge™ | Real Estate Deal Infrastructure Platform</title>
        <meta name="description" content="DealForge™ is a real estate deal infrastructure platform connecting acquisition, capital, execution, and closing in one system. Built for serious real estate operators." />
        <meta property="og:title" content="DealForge™ | Real Estate Deal Infrastructure Platform" />
        <meta property="og:description" content="DealForge™ is a real estate deal infrastructure platform connecting acquisition, capital, execution, and closing in one system." />
        <link rel="canonical" href="https://dealforge.pro/" />
      </Helmet>
    <div className="min-h-screen bg-black">
      {/* Referral Code Banner */}
      {referralCode && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-[#D4AF37] text-black text-center py-2 px-4">
          <p className="text-xs font-semibold">
            You were invited by a DealForge Network member — Code: <span className="font-mono font-bold">{referralCode}</span>
            {" "}· Apply below and your sponsor earns Network Credits when you become a paying member.
          </p>
        </div>
      )}
      {/* Navigation */}
      <nav className={`fixed ${referralCode ? "top-8" : "top-0"} left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-white/[0.06]/50`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <DealForgeLogo size="md" showTM={true} />
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection(waitlistRef)} className="text-white/50 hover:text-white transition-all duration-300">
                Private Beta
              </button>
              <a href="/login" className="text-white/50 hover:text-white transition-all duration-300">
                Login
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        {/* Gradient orbs */}
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-gold/10 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] pointer-events-none" />
        
        <div className="max-w-5xl mx-auto text-center relative">
          <div className="inline-flex items-center gap-2 bg-black/80 border border-gold/30 px-4 py-2 rounded-full mb-8">
            <Sparkles className="w-4 h-4 text-gold" />
            <span className="text-sm text-gold font-medium">Selective Beta · Application Required</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-2">
            DealForge™ — Real Estate Deal
            <span className="bg-gradient-to-r from-gold via-yellow-500 to-gold bg-clip-text text-transparent"> Infrastructure</span>
          </h1>
          <h2 className="text-2xl md:text-3xl font-bold text-white/70 mb-8">
            Built to Eliminate Execution Bottlenecks
          </h2>
          
          <p className="text-xl md:text-2xl text-white/60 max-w-3xl mx-auto mb-4 leading-relaxed">
            The execution layer for serious real estate operators<br />
            <span className="text-white/40">who are tired of losing deals to friction, delays, and disconnected systems.</span>
          </p>
          
          <p className="text-lg text-white/40 max-w-2xl mx-auto mb-8 leading-relaxed">
            Connect deal flow, capital, and execution in one system.
            Move from contract to close faster, with fewer breakdowns and less coordination overhead.
            Built for operators doing 10+ deals per year who want to scale without chaos.
          </p>
          
          {/* Scarcity indicators */}
          <div className="flex flex-wrap justify-center gap-6 mb-4 text-sm">
            <div className="flex items-center gap-2 text-white/60">
              <div className="w-2 h-2 bg-gold rounded-full" />
              <span>Application required</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <div className="w-2 h-2 bg-purple-500 rounded-full" />
              <span>Lock in founder pricing for life</span>
            </div>
            <div className="flex items-center gap-2 text-white/60">
              <div className="w-2 h-2 bg-emerald-500 rounded-full" />
              <span>Limited, selective beta access</span>
            </div>
          </div>
          
          <p className="text-gold font-medium mb-8">
            Founder pricing currently $99/month — locked for life upon acceptance.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => setShowFounderForm(true)}
              className="gold-gradient-btn h-14 px-10 text-lg font-bold"
            >
              Apply for Founder Access
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              onClick={() => scrollToSection(founderRef)}
              variant="outline"
              className="h-14 px-10 text-lg font-bold border-white/20 text-white/70 hover:bg-white/5 hover:text-white"
            >
              View Founder Terms
              <ChevronDown className="w-5 h-5 ml-2" />
            </Button>
          </div>
          
          <p className="mt-4 text-white/30 text-xs">
            Limited spots. Selective approval.
          </p>
          
          <p className="mt-6 text-white/50 text-sm font-medium border-t border-white/10 pt-6 max-w-xl mx-auto">
            Not for beginners. Not another CRM. This is deal execution infrastructure.
          </p>
        </div>
      </section>

      {/* Pain Points - Execution Friction */}
      <section className="py-24 bg-black/30 border-y border-white/[0.06]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Execution Friction <span className="text-[#FF3B30]">Kills Returns</span>
            </h2>
            <p className="text-xl text-white/60 mb-2">
              Not bad deals. Broken systems. Delays. Missed coordination.
            </p>
          </div>
          
          <div className="bg-black/50 border border-white/[0.06] p-8 rounded-2xl mb-8">
            <p className="text-lg text-white/70 leading-relaxed mb-6">
              Most investors don't lose money on bad deals —<br />
              <span className="text-white font-medium">they lose it between contract and close.</span>
            </p>
            <p className="text-white/50 leading-relaxed mb-6">
              Delays in funding, poor coordination, disconnected tools, and manual processes
              create inefficiencies that kill speed and reduce profitability.
            </p>
            <p className="text-white/60">
              <span className="text-gold font-medium">DealForge eliminates that friction</span> by connecting the entire deal process into one system.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {painPoints.map((point, idx) => (
              <div 
                key={idx}
                className="bg-black/50 border border-red-500/10 hover:border-red-500/30 p-5 rounded-2xl transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-red-500/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-red-500/20 transition-all duration-300">
                    <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-white mb-1">{point.text}</h3>
                    <p className="text-sm text-white/40">{point.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              One Platform. <span className="text-gold">Complete Control.</span>
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Unified transaction management from lead intake to capital exit.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, idx) => (
              <div 
                key={idx}
                className="bg-black/30 border border-white/[0.06] hover:border-gold/30 p-6 rounded-2xl transition-all group"
              >
                <div className="w-12 h-12 bg-gold/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-gold/20 transition-all duration-300">
                  <feature.icon className="w-6 h-6 text-gold" />
                </div>
                <h3 className="font-bold text-white text-lg mb-2">{feature.title}</h3>
                <p className="text-white/50">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What DealForge Is NOT - Clarity Section */}
      <section className="py-16 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12">
            {/* What it's NOT */}
            <div className="bg-red-500/5 border border-red-500/20 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-400" />
                DealForge is NOT
              </h3>
              <ul className="space-y-4">
                {[
                  { text: "Not a CRM" },
                  { text: "Not a course" },
                  { text: "Not a lead list" },
                  { text: "Not another tool" }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-red-400 text-sm">✕</span>
                    </div>
                    <span className="text-white/70">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* What it IS */}
            <div className="bg-emerald-500/5 border border-emerald-500/20 p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                <Check className="w-6 h-6 text-emerald-400" />
                What DealForge IS
              </h3>
              <ul className="space-y-4">
                {[
                  { text: "A real estate deal execution system" },
                  { text: "Infrastructure connecting deal flow, capital, and execution" },
                  { text: "Built to move deals from contract to close efficiently" }
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-emerald-400" />
                    </div>
                    <span className="text-white/70">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem / Network Map */}
      <section className="py-24 bg-black/30 border-y border-white/[0.06]/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              The <span className="text-gold">DealForge Network</span>
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto mb-3">
              Every real estate transaction requires multiple participants.
            </p>
            <p className="text-gold font-medium text-lg">
              DealForge connects them inside one execution platform.
            </p>
          </div>
          
          {/* Network Map Visualization */}
          <div className="flex justify-center mb-16">
            <NetworkMap variant="full" />
          </div>
          
          {/* Deal Flow Section */}
          <div className="mb-16">
            <h3 className="text-xl md:text-2xl font-bold text-white text-center mb-8">
              How Deals Move Through <span className="text-gold">DealForge</span>
            </h3>
            
            <div className="grid md:grid-cols-5 gap-4 relative">
              {/* Connection lines for desktop */}
              <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-0.5 bg-gradient-to-r from-gold/0 via-gold/50 to-gold/0" />
              
              {[
                { step: "1", title: "Acquire", desc: "Source deals, analyze returns, submit offers", color: "gold" },
                { step: "2", title: "Fund", desc: "Lenders compete for your deal with best terms", color: "green" },
                { step: "3", title: "Execute", desc: "Contractors bid, get scheduled, complete rehab", color: "blue" },
                { step: "4", title: "Close", desc: "Title coordinates closing, docs, and funding", color: "green" },
                { step: "5", title: "Exit", desc: "Sell or refinance, capture returns, repeat", color: "gold" }
              ].map((item, idx) => (
                <div key={idx} className="flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-3 relative z-10 ${
                    item.color === 'gold' ? 'bg-gold/10 border-2 border-gold' :
                    item.color === 'green' ? 'bg-emerald-500/10 border-2 border-emerald-500' :
                    'bg-blue-500/10 border-2 border-blue-500'
                  }`}>
                    <span className={`text-2xl font-bold ${
                      item.color === 'gold' ? 'text-gold' :
                      item.color === 'green' ? 'text-emerald-400' :
                      'text-blue-400'
                    }`}>{item.step}</span>
                  </div>
                  <h4 className="font-bold text-white mb-1">{item.title}</h4>
                  <p className="text-xs text-white/50 max-w-[140px]">{item.desc}</p>
                </div>
              ))}
            </div>
            
            <p className="text-center text-white/40 text-sm mt-8">
              One platform. Complete visibility. Maximum velocity.
            </p>
          </div>
          
          {/* Portal Cards below the map */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {ecosystem.map((item, idx) => {
              const colorClasses = {
                gold: "border-gold/20 hover:border-gold/50 bg-gold/5",
                green: "border-green-500/20 hover:border-green-500/50 bg-green-500/5",
                blue: "border-blue-500/20 hover:border-blue-500/50 bg-blue-500/5",
                orange: "border-orange-500/20 hover:border-orange-500/50 bg-orange-500/5",
                purple: "border-purple-500/20 hover:border-purple-500/50 bg-purple-500/5"
              };
              const iconColors = {
                gold: "text-gold",
                green: "text-green-400",
                blue: "text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]",
                orange: "text-orange-400",
                purple: "text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]"
              };
              return (
                <div 
                  key={idx}
                  className={`border p-5 rounded-2xl text-center transition-all ${colorClasses[item.color]}`}
                >
                  <item.icon className={`w-8 h-8 mx-auto mb-3 ${iconColors[item.color]}`} />
                  <h3 className="font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-xs text-white/40">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Founder Access Pricing Section */}
      <section ref={founderRef} className="py-24 bg-gradient-to-b from-purple-900/10 to-black border-y border-purple-500/20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 px-4 py-2 rounded-full mb-8">
            <Crown className="w-4 h-4 text-[#BF5AF2]" />
            <span className="text-sm text-purple-300 font-medium">Selective Access</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Founder Access Pricing
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto mb-12">
            Early operators accepted into DealForge secure founder-level pricing locked in for life.
          </p>
          
          <div className="bg-black/60 border border-purple-500/30 rounded-2xl p-8 mb-8 max-w-lg mx-auto">
            <div className="flex items-baseline justify-center gap-2 mb-2">
              <span className="text-5xl font-bold text-white">$99</span>
              <span className="text-white/50">/month</span>
            </div>
            <p className="text-purple-300 font-medium mb-4">Founder Rate</p>
            
            <div className="bg-purple-500/10 border border-purple-500/20 p-4 rounded-xl mb-6">
              <p className="text-white/60 text-sm">
                Pricing will increase as additional features, portals, and network effects expand.
                <span className="block mt-2 text-purple-300 font-medium">This pricing will not be available once DealForge opens publicly.</span>
              </p>
            </div>
            
            <h4 className="text-white/70 text-sm font-semibold uppercase tracking-wider mb-4">Why This Matters</h4>
            <ul className="text-left space-y-3 mb-8">
              {[
                "Lock in the lowest pricing tier permanently",
                "Gain access before network saturation",
                "Maintain a cost advantage as the platform grows",
                "Full platform access — all portals included",
                "Direct access to founding team",
                "Product roadmap influence"
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-white/70">
                  <Check className="w-4 h-4 text-[#BF5AF2] flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
            
            <Button 
              onClick={() => setShowFounderForm(true)}
              className="w-full h-14 text-lg font-bold bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-500 hover:to-purple-400"
            >
              Apply for Founder Access
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-white/40 text-xs mt-4">
              Limited spots. Selective approval.<br />
              Founder pricing locked upon acceptance.
            </p>
          </div>
        </div>
      </section>

      {/* Early Access Form */}
      <section ref={waitlistRef} className="py-24 bg-black/30 border-y border-white/[0.06]/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Left side - Benefits */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Request <span className="text-gold">Beta Access</span>
              </h2>
              <p className="text-white/50 mb-8">
                DealForge isn't for everyone. We're building infrastructure for serious operators 
                who want to scale their deal flow without scaling their headcount.
              </p>
              
              <div className="space-y-4 mb-8">
                <h4 className="text-sm font-semibold text-gold uppercase tracking-wider">What Operators Get</h4>
                {[
                  "Close more deals with less friction",
                  "Access capital faster through our lender network",
                  "One system instead of 5+ fragmented tools",
                  "Increase speed from contract to close"
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gold/10 rounded-2xl flex items-center justify-center">
                      <Check className="w-4 h-4 text-gold" />
                    </div>
                    <span className="text-white/70">{benefit}</span>
                  </div>
                ))}
              </div>

              <div className="bg-purple-500/10 border border-purple-500/20 p-4 rounded-2xl">
                <p className="text-purple-300 text-sm mb-2">
                  <Crown className="w-4 h-4 inline mr-2" />
                  <strong>Doing 10+ deals/year?</strong>
                </p>
                <p className="text-white/50 text-sm mb-3">
                  Skip the waitlist. Apply directly for Founder Beta and lock in $99/month for life.
                </p>
                <button 
                  onClick={() => setShowFounderForm(true)}
                  className="text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)] hover:text-purple-300 text-sm font-medium flex items-center gap-1"
                >
                  Apply for Founder Beta <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Right side - Form */}
            <div className="bg-black/80 border border-white/[0.06] p-8 rounded-2xl">
              <h3 className="text-xl font-bold text-white mb-2">
                Request Access
              </h3>
              <p className="text-white/40 text-sm mb-6">
                Get notified when we open public access.
              </p>

              <form onSubmit={handleWaitlistSubmit} className="space-y-5">
                <div>
                  <Label className="text-white/70 text-sm block mb-2">First Name *</Label>
                  <Input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-12 focus:border-gold"
                    data-testid="waitlist-firstname"
                  />
                </div>

                <div>
                  <Label className="text-white/70 text-sm block mb-2">Email Address *</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-12 focus:border-gold"
                    data-testid="waitlist-email"
                  />
                </div>

                <div>
                  <Label className="text-white/70 text-sm block mb-2">What type of investor are you? *</Label>
                  <Select value={investorType} onValueChange={setInvestorType}>
                    <SelectTrigger className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-12 focus:border-gold" data-testid="waitlist-investor-type">
                      <SelectValue placeholder="Select investor type" />
                    </SelectTrigger>
                    <SelectContent className="bg-black border-white/[0.08]">
                      <SelectItem value="wholesaler">Wholesaler</SelectItem>
                      <SelectItem value="fix_flip">Fix & Flip</SelectItem>
                      <SelectItem value="buy_hold">Buy & Hold</SelectItem>
                      <SelectItem value="creative_finance">Creative Finance</SelectItem>
                      <SelectItem value="agent_investor">Agent-Investor</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white/70 text-sm block mb-2">Primary Market *</Label>
                  <Input
                    type="text"
                    value={market}
                    onChange={(e) => setMarket(e.target.value)}
                    placeholder="e.g. Charlotte, NC"
                    className="bg-[#0A0A0A]/80 backdrop-blur-sm border-white/[0.08] text-white h-12 focus:border-gold"
                    data-testid="waitlist-market"
                  />
                </div>

                {error && <p className="text-[#FF3B30] text-sm">{error}</p>}

                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full gold-gradient-btn h-12 font-bold"
                  data-testid="waitlist-submit"
                >
                  {isSubmitting ? "Joining..." : "Join Waitlist"}
                </Button>
              </form>

              <p className="text-white/40 text-xs mt-4 text-center">
                No spam, ever. Unsubscribe anytime.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/[0.06]/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-8">
            <div className="text-center md:text-left">
              <DealForgeLogo size="md" showTM={true} />
              <p className="text-white/40 text-sm mt-2">
                DealForge real estate software — the infrastructure for deal execution.
              </p>
            </div>
            
            <div className="flex items-center gap-6">
              <Link to="/what-is-dealforge" className="text-white/50 hover:text-white text-sm transition-all">
                What is DealForge?
              </Link>
              <a href="/login" className="text-white/50 hover:text-white text-sm transition-all">
                Login
              </a>
            </div>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <span className="text-white/30 text-xs">Follow DealForge:</span>
              <a 
                href="https://www.linkedin.com/company/dealforge%E2%84%A2-patent-pending/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all"
                aria-label="DealForge on LinkedIn"
              >
                <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                </svg>
              </a>
              <a 
                href="https://x.com/DealForgeInc" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all"
                aria-label="DealForge on X"
              >
                <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a 
                href="https://www.facebook.com/share/15eeuUtpPyt/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all"
                aria-label="DealForge on Facebook"
              >
                <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div className="pt-6 border-t border-white/[0.06] text-center">
            <p className="text-white/40 text-sm">
              © 2026 DealForge Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
